/**
 * Patient Service
 *
 * This service provides a unified API for patient data operations,
 * handling the differences between Production and FHIR/Greenfield modes.
 *
 * For Greenfield operations, it delegates to the dedicated greenfieldService.
 */

import { normalizePatientData } from '../adapters/patientDataAdapter';
import { getConfig, setEnvironmentMode } from '../config/environmentConfig';
import * as greenfieldService from './greenfieldService';
import {
  getDemoPatient,
} from '../utils/demoDataUtils';
import { apiUtils } from '../utils/apiUtils';
import { getRules as getRulesFromApi } from '../api/rulesApi';

/**
 * Returns fallback patient data for production mode
 * @returns {Array} Array of fallback patient data
 */
function getFallbackPatients() {
  return [
    {
      id: 'VP0000001',
      firstName: 'John',
      lastName: 'Doe',
      surgeryType: 'Knee Replacement',
      dob: '1970-01-01T00:00:00.000Z',
      _source: 'production',
    },
    {
      id: 'VP0000002',
      firstName: 'Jane',
      lastName: 'Smith',
      surgeryType: 'Hip Replacement',
      dob: '1980-05-15T00:00:00.000Z',
      _source: 'production',
    },
    {
      id: 'VP0000003',
      firstName: 'Robert',
      lastName: 'Johnson',
      surgeryType: 'Spinal Fusion',
      dob: '1965-09-23T00:00:00.000Z',
      _source: 'production',
    },
  ];
}


/**
 * Fetches a patient by ID and normalizes the data structure
 * @param {string} patientId - The ID of the patient to fetch
 * @returns {Promise<Object>} - Normalized patient data
 */
export async function getPatient(patientId) {
  const config = getConfig();

  // Force a refresh of the environment config to ensure we have the correct mode
  const currentMode = localStorage.getItem('environmentMode') || 'production';
  if (currentMode !== config.mode) {
    setEnvironmentMode(currentMode);
  }

  // Define fallback patient data based on ID
  const fallbackPatients = {
    VP0000001: {
      id: 'VP0000001',
      firstName: 'John',
      lastName: 'Doe',
      surgeryType: 'Knee Replacement',
      dob: '1970-01-01T00:00:00.000Z',
      _source: 'production',
    },
    VP0000002: {
      id: 'VP0000002',
      firstName: 'Jane',
      lastName: 'Smith',
      surgeryType: 'Hip Replacement',
      dob: '1980-05-15T00:00:00.000Z',
      _source: 'production',
    },
    VP0000003: {
      id: 'VP0000003',
      firstName: 'Robert',
      lastName: 'Johnson',
      surgeryType: 'Spinal Fusion',
      dob: '1965-09-23T00:00:00.000Z',
      _source: 'production',
    },
  };

  // Create a generic fallback patient if ID doesn't match
  const genericPatient = {
    id: patientId,
    firstName: 'Production',
    lastName: 'Patient',
    surgeryType: 'General Surgery',
    dob: '1975-06-15T00:00:00.000Z',
    _source: 'production',
  };

  // Get fallback patient
  const getFallbackPatient = () => fallbackPatients[patientId] || genericPatient;

  try {
    // In greenfield mode, use the regular API without production fallbacks
    if (config.mode === 'greenfield') {
      try {
        const patientData = await greenfieldService.getGreenfieldPatient(patientId);
        if (!patientData) {
          throw new Error(`Patient ${patientId} not found`);
        }
        return patientData;
      } catch (error) {
        console.error(`[PatientService] Error fetching patient ${patientId}:`, error);
        throw error; // In production, always throw errors - no fallback
      }
    }

    // In production or hospital mode, always use API
    if (config.mode === 'production' || config.mode === 'hospital') {
      // Determine if we're running locally or in production
      const isLocal =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';


      try {
        // Always use API in production mode
        const response = await apiUtils.get(`/api/patients/${patientId}`);

        // Process the response data
        let patientData = null;

        // apiUtils returns response.data, which is { success: true, data: {...} }
        // So we need to check multiple possible structures
        if (response && response.id) {
          // Direct patient object
          patientData = response;
        } else if (response && response.data && response.data.data && response.data.data.id) {
          // Double-wrapped (response.data.data contains patient)
          patientData = response.data.data;
        } else if (response && response.success && response.data && response.data.id) {
          // Wrapped with success flag
          patientData = response.data;
        } else if (response && response.data && response.data.id) {
          // Wrapped in data property
          patientData = response.data;
        }

        // If we found valid data, normalize it
        if (patientData) {
          const normalizedPatient = normalizePatientData(patientData, 'production');
          normalizedPatient._source = 'production';
          return normalizedPatient;
        }

        // If data is not in expected format, throw error in production
        console.warn(`[PatientService] Response data for patient ${patientId} is not valid`);
        if (!isLocal) {
          throw new Error(`Patient ${patientId} not found`);
        }
        // Only use fallback in local development
        return getFallbackPatient();
      } catch (corsError) {
        console.error(
          `[PatientService] CORS or network error for patient ${patientId}:`,
          corsError
        );

        // In production, throw the error
        if (!isLocal) {
          throw corsError;
        }
        // Only use fallback data in local development
        console.warn(
          `[PatientService] Using fallback production data for patient ${patientId} in local development`
        );
        return getFallbackPatient();
      }
    }

    // If we reach here, something went wrong with the mode detection
    console.warn(`[PatientService] Unexpected mode for patient ${patientId}`);
    throw new Error(`Patient ${patientId} not found`);
  } catch (error) {
    console.error(`[PatientService] Error fetching patient ${patientId}:`, error);

    // In production or hospital mode, only use fallback data in local development
    if (config.mode === 'production' || config.mode === 'hospital') {
      const isLocal =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal) {
        console.warn(
          `[PatientService] Error in production mode - returning fallback data for patient ${patientId} in local development`
        );
        return getFallbackPatient();
      }
    }

    throw error;
  }
}

/**
 * Fetches all patients and normalizes their data structure
 * @returns {Promise<Array>} - Array of normalized patient data
 */
export async function getPatients() {
  // Force a refresh of the environment config to ensure we have the correct mode
  const currentMode = localStorage.getItem('environmentMode') || 'production';
  const config = getConfig();

  if (currentMode !== config.mode) {
    setEnvironmentMode(currentMode);
  }

  // Define fallback data for both local and production environments
  const fallbackPatients = [
    {
      id: 'VP0000001',
      firstName: 'John',
      lastName: 'Doe',
      surgeryType: 'Knee Replacement',
      dob: '1970-01-01T00:00:00.000Z',
      _source: 'production',
    },
    {
      id: 'VP0000002',
      firstName: 'Jane',
      lastName: 'Smith',
      surgeryType: 'Hip Replacement',
      dob: '1980-05-15T00:00:00.000Z',
      _source: 'production',
    },
    {
      id: 'VP0000003',
      firstName: 'Robert',
      lastName: 'Johnson',
      surgeryType: 'Spinal Fusion',
      dob: '1965-09-23T00:00:00.000Z',
      _source: 'production',
    },
  ];

  try {
    // In greenfield mode, use the regular API without production fallbacks
    if (config.mode === 'greenfield') {
      try {
        return await greenfieldService.getGreenfieldPatients();
      } catch (error) {
        console.error('[PatientService] Greenfield API error:', error);
        throw error; // In greenfield mode, always throw errors - no fallback
      }
    }

    // In hospital mode, use the regular API
    if (config.mode === 'hospital') {

      try {
        // Use apiUtils which handles authentication properly
        const response = await apiUtils.get('/api/patients');

        // Process the response data
        let patientData = [];

        // apiUtils wraps the response: { success: true, data: <backend_response> }
        // Backend returns: { patients: [...], total, limit, offset }
        // So we need to check multiple possible structures
        if (response.success && response.data) {
          if (response.data.patients && Array.isArray(response.data.patients)) {
            // Backend returns { patients: [...] }
            patientData = response.data.patients;
          } else if (Array.isArray(response.data.data)) {
            patientData = response.data.data;
          } else if (Array.isArray(response.data)) {
            // Fallback if backend structure changes
            patientData = response.data;
          }
        } else if (response && response.patients && Array.isArray(response.patients)) {
          // Direct response with patients array
          patientData = response.patients;
        } else if (!response.success && response.status === 401) {
          // Don't warn about 401 errors - they're expected when not authenticated
          return [];
        } else {
          console.warn(
            '[PatientService] Response data is not in expected format, returning empty array'
          );
          console.warn('[PatientService] Actual response:', response);
          return [];
        }

        // Normalize the data
        const normalizedPatients = patientData.map(patient => {
          const normalizedPatient = normalizePatientData(patient, 'hospital');
          // Set _source to 'api' for hospital mode patients from the API
          normalizedPatient._source = 'api';
          return normalizedPatient;
        });

        return normalizedPatients;
      } catch (error) {
        console.error('[PatientService] API error:', error);
        throw error; // In hospital mode, always throw errors - no fallback
      }
    }

    // In production or hospital mode, always use API first
    if (config.mode === 'production' || config.mode === 'hospital') {
      // Determine if we're running locally or in production
      const isLocal =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';


      try {
        // Always try API first in production mode
        const response = await apiUtils.get('/api/patients');

        // Process the response data
        let patientData = [];

        // apiUtils wraps the response: { success: true, data: <backend_response> }
        // Backend returns: { patients: [...], total, limit, offset }
        // So we need to check multiple possible structures
        if (response.success && response.data) {
          if (response.data.patients && Array.isArray(response.data.patients)) {
            // Backend returns { patients: [...] }
            patientData = response.data.patients;
          } else if (Array.isArray(response.data.data)) {
            patientData = response.data.data;
          } else if (Array.isArray(response.data)) {
            // Fallback if backend structure changes
            patientData = response.data;
          }
        }
        // Check direct response format
        else if (response && response.patients && Array.isArray(response.patients)) {
          // Direct response with patients array
          patientData = response.patients;
        }
        // If we can't find valid data, return empty array instead of fallback
        else if (!response.success && response.status === 401) {
          // Don't warn about 401 errors - they're expected when not authenticated
          return [];
        } else {
          console.warn(
            '[PatientService] Response data is not in expected format, returning empty array'
          );
          console.warn('[PatientService] Actual response:', response);
          return [];
        }

        // Normalize the data
        const normalizedPatients = patientData.map(patient => {
          const normalizedPatient = normalizePatientData(patient, 'production');
          normalizedPatient._source = 'production'; // Explicitly mark as production source
          return normalizedPatient;
        });

        return normalizedPatients;
      } catch (corsError) {
        console.error('[PatientService] API error:', corsError);

        // In production/hospital mode, don't use fallback data - throw the error
        if (!isLocal) {
          throw corsError;
        }

        // Only use fallback data in local development
        console.warn('[PatientService] Using fallback production data in local development');
        return fallbackPatients;
      }
    }

    // If we reach here, something went wrong with the mode detection
    console.warn('[PatientService] Unexpected mode');
    return [];
  } catch (error) {
    console.error('[PatientService] Error fetching patients:', error);

    // In production or hospital mode, only use fallback data in local development
    if (config.mode === 'production' || config.mode === 'hospital') {
      const isLocal =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal) {
        console.warn(
          '[PatientService] Error in production mode - returning fallback data for local development'
        );
        return fallbackPatients;
      }
    }

    // In Greenfield mode, propagate the error
    throw error;
  }
}

/**
 * Fetches a patient's preop summary
 * @param {string} patientId - The ID of the patient
 * @returns {Promise<Object>} - Normalized preop summary data
 */
export async function getPreopSummary(patientId) {
  const config = getConfig();

  // Helper function to calculate age from DOB
  const calculateAge = dob => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Define function to create preop summary from patient data
  const createPreopSummary = patientData => {
    return {
      patientId,
      demographics: {
        age: patientData.dob ? calculateAge(patientData.dob) : 45,
        gender: patientData.gender || 'M',
        height: patientData.height || 175,
        weight: patientData.weight || 80,
        bmi: patientData.bmi || 26.1,
      },
      preOpAssessment: {
        generalHealth: 'Good',
        allergies: patientData.allergies || ['None reported'],
        medications: patientData.medications || ['Acetaminophen as needed'],
        pastSurgicalHistory: 'None',
        familyHistory: 'Non-contributory',
        physicalExam: 'Within normal limits',
        labResults: 'All values within normal range',
        imaging: 'No significant findings',
        anesthesiaConsiderations: `ASA Class ${patientData.asaScore || 'II'}`,
        surgicalPlan: 'Standard approach',
        recommendations: 'Proceed as planned',
      },
      recentVitals: { bp: '120/80', hr: 72 },
      keyDiagnoses: patientData.comorbidities || ['Hypertension'],
      flags: [],
      // Include lab values from patient data
      labs: patientData.labs || {
        hemoglobin: 14.0,
        hematocrit: 42,
        platelets: 230,
        na: 140,
        k: 4.1,
        hco3: 24,
        creatinine: 1.0,
        glu: 100,
        inr: 1.0,
      },
      // Include medications
      medications: patientData.medications || ['Lisinopril', 'Aspirin'],
      // Include allergies
      allergies: patientData.allergies || ['Penicillin'],
      // Include ASA score
      asaScore: patientData.asaScore || '2',
      _source: 'production',
    };
  };

  // Define fallback preop summary data with comprehensive information
  const getFallbackPreopSummary = () => {
    // Get the production patient data to extract relevant information
    const productionPatient = getDemoPatient(patientId);
    return createPreopSummary(productionPatient);
  };

  // In production/greenfield mode, fetch real patient data and create summary
  if (config.mode === 'greenfield') {
    try {
      const patient = await getPatient(patientId);
      return createPreopSummary(patient);
    } catch (error) {
      console.error(
        `[PatientService] Error fetching patient for preop summary ${patientId}:`,
        error
      );
      throw error;
    }
  }

  // In production or hospital mode, first check localStorage for updated patient data
  if (config.mode === 'production' || config.mode === 'hospital') {
    try {
      // Check localStorage for any persisted patient data
      const storedPatientsStr = localStorage.getItem('demoPatients');
      if (storedPatientsStr) {
        const storedPatients = JSON.parse(storedPatientsStr);
        if (Array.isArray(storedPatients)) {
          const storedPatient = storedPatients.find(p => p.id === patientId);
          if (storedPatient) {
            return createPreopSummary(storedPatient);
          }
        }
      }
    } catch (storageError) {
      console.warn(
        `[PatientService] Error reading patient ${patientId} from localStorage for preop summary:`,
        storageError
      );
    }

    // If no localStorage data found, use fallback
    return getFallbackPreopSummary();
  }

  // If we reach here, something went wrong with the mode detection
  console.warn(
    `[PatientService] Unexpected mode, using fallback preop data for patient ${patientId}`
  );
  return getFallbackPreopSummary();
}

/**
 * Fetches a patient's preop summary and normalizes it
 * @param {string} patientId - The ID of the patient
 * @returns {Promise<Object>} - Normalized summary data
 */
export async function getPatientSummary(patientId) {
  const config = getConfig();

  // Define fallback summary data
  const getFallbackSummary = async () => {
    const patient = await getPatient(patientId);
    return {
      patientId,
      patientName: `${patient.firstName} ${patient.lastName}`,
      surgeryType: patient.surgeryType || 'General Surgery',
      surgeryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      surgeon: 'Dr. Production Surgeon',
      medicalHistory: {
        conditions: ['None reported'],
        medications: ['None reported'],
        allergies: ['None reported'],
      },
      vitals: {
        height: '175 cm',
        weight: '70 kg',
        bmi: '22.9',
        bloodPressure: '120/80',
        heartRate: '72',
        temperature: '98.6°F',
      },
      aiInsights: {
        riskFactors: ['None identified'],
        recommendations: ['Standard pre-operative protocol'],
        alerts: [],
      },
      flags: [],
      labs: {},
      _source: 'production',
    };
  };

  // In production/greenfield mode, fetch real patient data and create summary
  if (config.mode === 'greenfield') {
    try {
      const patient = await getPatient(patientId);
      return {
        patientId,
        patientName: `${patient.firstName} ${patient.lastName}`,
        surgeryType: patient.surgeryType || patient.surgery || 'General Surgery',
        surgeryDate:
          patient.surgeryDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        surgeon: patient.surgeon || patient.surgeonName || 'Dr. Assigned Surgeon',
        medicalHistory: {
          conditions: patient.comorbidities || ['None reported'],
          medications: patient.medications || ['None reported'],
          allergies: patient.allergies || ['None reported'],
        },
        vitals: {
          height: patient.height || '175 cm',
          weight: patient.weight || '70 kg',
          bmi: patient.bmi || '22.9',
          bloodPressure: patient.bloodPressure || '120/80',
          heartRate: patient.heartRate || '72',
          temperature: patient.temperature || '98.6°F',
        },
        aiInsights: patient.aiInsights || {
          riskFactors: ['None identified'],
          recommendations: ['Standard pre-operative protocol'],
          alerts: [],
        },
        flags: patient.flags || [],
        labs: patient.labs || {},
        _source: 'api',
      };
    } catch (error) {
      console.error(`[PatientService] Error fetching patient for summary ${patientId}:`, error);
      throw error;
    }
  }

  // In production or hospital mode, first check localStorage for real patient data
  if (config.mode === 'production' || config.mode === 'hospital') {
    try {
      // Check localStorage for any persisted patient data
      const storedPatientsStr = localStorage.getItem('demoPatients');
      if (storedPatientsStr) {
        const storedPatients = JSON.parse(storedPatientsStr);
        if (Array.isArray(storedPatients)) {
          const storedPatient = storedPatients.find(p => p.id === patientId);
          if (storedPatient) {
            // Create summary from real patient data
            return {
              patientId,
              patientName: `${storedPatient.firstName} ${storedPatient.lastName}`,
              surgeryType: storedPatient.surgeryType || storedPatient.surgery || 'General Surgery',
              surgeryDate:
                storedPatient.surgeryDate ||
                new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              surgeon:
                storedPatient.surgeon || storedPatient.surgeonName || 'Dr. Production Surgeon',
              medicalHistory: {
                conditions: storedPatient.comorbidities || ['None reported'],
                medications: storedPatient.medications || ['None reported'],
                allergies: storedPatient.allergies || ['None reported'],
              },
              vitals: {
                height: storedPatient.height || '175 cm',
                weight: storedPatient.weight || '70 kg',
                bmi: storedPatient.bmi || '22.9',
                bloodPressure: storedPatient.bloodPressure || '120/80',
                heartRate: storedPatient.heartRate || '72',
                temperature: storedPatient.temperature || '98.6°F',
              },
              aiInsights: storedPatient.aiInsights || {
                riskFactors: ['None identified'],
                recommendations: ['Standard pre-operative protocol'],
                alerts: [],
              },
              flags: storedPatient.flags || [],
              labs: storedPatient.labs || {},
              _source: 'production',
            };
          }
        }
      }
    } catch (storageError) {
      console.warn(
        `[PatientService] Error reading patient ${patientId} from localStorage for summary:`,
        storageError
      );
    }

    // If no localStorage data found, use fallback
    return getFallbackSummary();
  }

  // If we reach here, something went wrong with the mode detection
  console.warn(
    `[PatientService] Unexpected mode, using fallback summary data for patient ${patientId}`
  );
  return getFallbackSummary();
}

/**
 * Creates a new patient
 * @param {Object} patientData - The patient data to create
 * @returns {Promise<Object>} - The created patient
 */
export async function createPatient(patientData) {
  const config = getConfig();

  // Ensure array fields are properly maintained
  const ensureArrayFields = (data) => {
    const arrayFields = ['allergies', 'medications', 'comorbidities'];
    const result = { ...data };
    
    arrayFields.forEach(field => {
      if (result[field] !== undefined && !Array.isArray(result[field])) {
        console.warn(`[PatientService] ${field} is not an array:`, result[field]);
        // Try to parse if it's a JSON string
        if (typeof result[field] === 'string') {
          try {
            const parsed = JSON.parse(result[field]);
            result[field] = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            result[field] = [];
          }
        } else {
          result[field] = [];
        }
      }
    });
    
    return result;
  };

  // In production mode, create a mock patient with ID
  if (config.mode === 'production') {
    // Generate a random ID for the production patient in VP format
    const productionId = `VP${Math.floor(Math.random() * 10000000)
      .toString()
      .padStart(7, '0')}`;
    const createdPatient = {
      ...ensureArrayFields(patientData),
      id: productionId,
      _source: 'production',
      createdAt: new Date().toISOString(),
    };

    // Persist to localStorage for demo mode
    try {
      const storedPatientsStr = localStorage.getItem('demoPatients');
      let existingPatients = storedPatientsStr ? JSON.parse(storedPatientsStr) : [];
      
      // Ensure existingPatients is an array
      if (!Array.isArray(existingPatients)) {
        console.warn('[PatientService] existingPatients is not an array:', existingPatients);
        existingPatients = [];
      }
      
      const updatedPatients = [...existingPatients, createdPatient];
      localStorage.setItem('demoPatients', JSON.stringify(updatedPatients));
    } catch (error) {
      console.warn('[PatientService] Failed to persist production patient to localStorage:', error);
    }

    return normalizePatientData(createdPatient, 'production');
  }

  // For Greenfield/Hospital mode, continue with API call

  try {
    // Transform the data to match backend expectations
    const transformedData = {
      mrn: patientData.mrn || `MRN${Date.now()}`,
      firstName: patientData.firstName,
      lastName: patientData.lastName,
      dateOfBirth: patientData.dob || patientData.dateOfBirth,
      gender: patientData.gender,
      height: parseInt(patientData.height) || 0,
      weight: parseInt(patientData.weight) || 0,
      allergies: patientData.allergies || [],
      medications: patientData.medications || [],
      medicalHistory: patientData.comorbidities || patientData.medicalHistory || [],
      asaScore: patientData.asaScore,
      labs: patientData.labs,
      ekg: patientData.ekg,
      chestXray: patientData.chestXray,
      surgery: patientData.surgeryDate ? {
        surgeryType: patientData.surgeryType,
        surgeryName: patientData.surgeryType,
        surgeryDate: patientData.surgeryDate,
        surgeonId: patientData.surgeonId
      } : null
    };
    
    // Use apiUtils which handles authentication properly
    const response = await apiUtils.post('/api/patients', ensureArrayFields(transformedData));

    // apiUtils wraps the response: { success: true, data: <backend_response> }
    if (response.success || response.id) {
      // Backend returns {id, message} on success, so reconstruct the patient data
      const createdPatient = {
        ...transformedData,
        id: response.id || response.data?.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return normalizePatientData(createdPatient, config.mode);
    } else {
      throw new Error(response.error || response.details || 'Failed to create patient');
    }
  } catch (error) {
    console.error('[PatientService] Error creating patient:', error);
    throw error;
  }
}

/**
 * Updates an existing patient
 * @param {string} patientId - The ID of the patient to update
 * @param {Object} patientData - The updated patient data
 * @returns {Promise<Object>} - The updated patient
 */
export async function updatePatient(patientId, patientData) {
  let config = getConfig();

  // Keep environment config in sync with localStorage (e.g., hospital mode)
  const currentMode = localStorage.getItem('environmentMode');
  if (currentMode && currentMode !== config.mode) {
    setEnvironmentMode(currentMode);
    config = getConfig();
  }

  // PRODUCTION mode and API mode both update localStorage for patient updates
  // Always persist to localStorage and return normalized patient
  
  // Ensure array fields are properly maintained
  const ensureArrayFields = (data) => {
    const arrayFields = ['allergies', 'medications', 'comorbidities'];
    const result = { ...data };
    
    arrayFields.forEach(field => {
      if (result[field] !== undefined && !Array.isArray(result[field])) {
        console.warn(`[PatientService] ${field} is not an array:`, result[field]);
        // Try to parse if it's a JSON string
        if (typeof result[field] === 'string') {
          try {
            const parsed = JSON.parse(result[field]);
            result[field] = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            result[field] = [];
          }
        } else {
          result[field] = [];
        }
      }
    });
    
    return result;
  };
  
  let updatedPatient;
  try {
    const existingPatient = await getPatient(patientId);
    updatedPatient = {
      ...existingPatient,
      ...ensureArrayFields(patientData),
      id: patientId, // Ensure ID doesn't change
      _source: config.mode,
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    // If patient doesn't exist, treat as new (for demo mode)
    updatedPatient = {
      ...ensureArrayFields(patientData),
      id: patientId,
      _source: config.mode,
      updatedAt: new Date().toISOString(),
    };
  }
  // Persist to localStorage
  try {
    const storedPatientsStr = localStorage.getItem('demoPatients');
    let storedPatients = storedPatientsStr ? JSON.parse(storedPatientsStr) : [];
    
    // Ensure storedPatients is an array
    if (!Array.isArray(storedPatients)) {
      console.warn(`[PatientService] storedPatients is not an array:`, storedPatients);
      storedPatients = [];
    }
    
    const patientIndex = storedPatients.findIndex(p => p.id === patientId);
    if (patientIndex >= 0) {
      storedPatients[patientIndex] = updatedPatient;
    } else {
      storedPatients.push(updatedPatient);
    }
    localStorage.setItem('demoPatients', JSON.stringify(storedPatients));
  } catch (storageError) {
    console.warn(
      `[PatientService] Failed to persist patient update to localStorage:`,
      storageError
    );
  }
  // If API mode (hospital or greenfield), also send to backend
  if (config.mode === 'hospital' || config.mode === 'greenfield') {
    try {
      // Use apiUtils which handles authentication properly
      // Move ensureArrayFields definition outside if not available in this scope
      const ensureArrayFieldsForAPI = (data) => {
        const arrayFields = ['allergies', 'medications', 'comorbidities'];
        const result = { ...data };
        
        arrayFields.forEach(field => {
          if (result[field] !== undefined && !Array.isArray(result[field])) {
            console.warn(`[PatientService] ${field} is not an array:`, result[field]);
            // Try to parse if it's a JSON string
            if (typeof result[field] === 'string') {
              try {
                const parsed = JSON.parse(result[field]);
                result[field] = Array.isArray(parsed) ? parsed : [];
              } catch (e) {
                result[field] = [];
              }
            } else {
              result[field] = [];
            }
          }
        });
        
        return result;
      };
      
      const response = await apiUtils.put(`/api/patients/${patientId}`, ensureArrayFieldsForAPI(patientData));

      // apiUtils wraps the response: { success: true, data: <backend_response> }
      if (response.success && response.data) {
        return normalizePatientData(response.data, config.mode);
      } else {
        throw new Error(response.error || 'Failed to update patient');
      }
    } catch (error) {
      console.error(`[PatientService] Error updating patient ${patientId}:`, error);
      throw error;
    }
  }

  // For production/demo mode, return the locally updated patient
  return normalizePatientData(updatedPatient, config.mode);
}

/**
 * Deletes a patient
 * @param {string} patientId - The ID of the patient to delete
 * @returns {Promise<void>}
 */
export async function deletePatient(patientId) {
  const config = getConfig();

  // PRODUCTION mode and API mode both remove patient from localStorage
  try {
    const storedPatientsStr = localStorage.getItem('demoPatients');
    let storedPatients = storedPatientsStr ? JSON.parse(storedPatientsStr) : [];
    // Ensure storedPatients is an array before filtering
    if (Array.isArray(storedPatients)) {
      storedPatients = storedPatients.filter(p => p.id !== patientId);
      localStorage.setItem('demoPatients', JSON.stringify(storedPatients));
    } else {
      console.warn(`[PatientService] storedPatients is not an array:`, storedPatients);
      localStorage.setItem('demoPatients', JSON.stringify([]));
    }
  } catch (storageError) {
    console.warn(`[PatientService] Failed to delete patient from localStorage:`, storageError);
  }
  if (config.mode === 'production') return Promise.resolve();

  // For Greenfield/Hospital mode, continue with API call
  try {
    // Use apiUtils which handles authentication properly
    await apiUtils.delete(`/api/patients/${patientId}`);
  } catch (error) {
    console.error('[PatientService] Error deleting patient via API:', error);
    throw error;
  }
}

// New: Upsert coded lists via Worker endpoints
export async function upsertPatientMedications(patientId, items) {
  const res = await apiUtils.post(`/api/patients/${encodeURIComponent(patientId)}/medications`, { items });
  if (res.success === false) throw new Error(res.error || 'Failed to save medications');
  return res;
}

export async function upsertPatientConditions(patientId, items) {
  const res = await apiUtils.post(`/api/patients/${encodeURIComponent(patientId)}/conditions`, { items });
  if (res.success === false) throw new Error(res.error || 'Failed to save conditions');
  return res;
}

export async function upsertPatientAllergies(patientId, items) {
  const res = await apiUtils.post(`/api/patients/${encodeURIComponent(patientId)}/allergies`, { items });
  if (res.success === false) throw new Error(res.error || 'Failed to save allergies');
  return res;
}

export async function upsertPatientLabs(patientId, items) {
  const res = await apiUtils.post(`/api/patients/${encodeURIComponent(patientId)}/labs`, { items });
  if (res.success === false) throw new Error(res.error || 'Failed to save labs');
  return res;
}

/**
 * Fetches surgeons based on environment configuration.
 * @returns {Promise<Array>} - Array of surgeon data (structure depends on source but should be simple).
 */
export async function getSurgeons() {
  const config = getConfig();

  // Define fallback surgeons data
  const getFallbackSurgeons = () => {
    return [
      { id: 'S001', name: 'Dr. Sarah Johnson', specialty: 'Orthopedic' },
      { id: 'S002', name: 'Dr. Michael Chen', specialty: 'General' },
      { id: 'S003', name: 'Dr. Emily Rodriguez', specialty: 'Neurosurgery' },
      { id: 'S004', name: 'Dr. David Williams', specialty: 'Cardiothoracic' },
      { id: 'S005', name: 'Dr. Lisa Thompson', specialty: 'Vascular' },
    ];
  };

  // In production mode, immediately return fallback data without making API calls
  if (config.mode === 'production') {
    return getFallbackSurgeons();
  }

  // For Greenfield/Hospital mode, continue with API call

  try {
    // Use apiUtils which handles authentication properly
    const response = await apiUtils.get('/api/surgeons');

    // Process the response data
    let surgeonData = [];

    // apiUtils wraps the response: { success: true, data: <backend_response> }
    // Backend returns: { success: true, data: [...surgeons] }
    if (response.success && response.data) {
      if (Array.isArray(response.data.data)) {
        surgeonData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Fallback if backend structure changes
        surgeonData = response.data;
      }
    }

    if (surgeonData.length === 0) {
      console.warn('[PatientService] No surgeons returned from API, using fallback data');
      return getFallbackSurgeons();
    }

    return surgeonData;
  } catch (error) {
    console.warn(
      '[PatientService] Error fetching surgeons. Falling back to mock data. Error:',
      error.message
    );
    // Fallback to mock data
    return getFallbackSurgeons();
  }
}

/**
 * Fetches available surgery types based on environment configuration.
 * @returns {Promise<Array>} - Array of surgery type strings.
 */
export async function getSurgeryTypes() {
  const config = getConfig();

  // Define standard surgery types
  const standardSurgeryTypes = [
    'General',
    'Orthopedic',
    'Cardiothoracic',
    'Neurosurgery',
    'Vascular',
    'ENT',
    'Plastic',
    'Urology',
    'Gynecology',
    'Ophthalmology',
    'Other',
  ];

  // In production mode, immediately return standard types
  if (config.mode === 'production') {
    return standardSurgeryTypes;
  }

  // For Greenfield mode, we could potentially fetch from an API
  // But for now, we'll use the same hardcoded list to avoid API call issues
  return standardSurgeryTypes;
}

// Stub for getPatientsWithUrl - to be implemented if still needed
export async function getPatientsWithUrl(url) {
  // This function was likely for Greenfield pagination.  The main getPatients should handle this now
  // based on current logic in PatientManagementPage, or it needs specific implementation.
  return { patients: [], nextLink: null, prevLink: null, total: 0 };
}

// Get audit trail for a patient
export async function getAuditTrail(patientId) {
  try {
    const { getPatientAuditTrail } = await import('../api/auditApi');
    const response = await getPatientAuditTrail(patientId);
    return response.data || [];
  } catch (error) {
    console.error(`[PatientService] Error fetching audit trail for patient ${patientId}:`, error);
    return [];
  }
}

// Export the getRules function from rulesApi
export const getRules = getRulesFromApi;

export default {
  getPatient,
  getPatients,
  getPatientSummary,
  getPreopSummary,
  createPatient,
  updatePatient,
  deletePatient,
  getSurgeons,
  getSurgeryTypes,
  getPatientsWithUrl, // Added export
  getAuditTrail, // Added export
  getRules, // Added export
};
